import React, { useState } from "react";
import { FaUserSecret } from "react-icons/fa";
import { toast } from "react-toastify";

import { apiStartVendorImpersonation } from "../api";
import * as impersonation from "../utilities/impersonation";
import { resolveLandingRoute } from "../utilities/landingRoute";
import { getUserFromLocalStorage } from "../utilities/user";

/**
 * SignInAsVendorButton
 * ─────────────────────────────────────────────────────────────────────────────
 * "Sign in as vendor" for any directory row: restaurant, retailer/wholesaler,
 * pharmacy or brand. One component so the four directories share the call,
 * the session swap and the landing, and none of them grows its own copy.
 *
 *   <SignInAsVendorButton ownerId={r.owner} vendorId={r._id} vendorName={r.name}>
 *     {({ open, busy }) => <button onClick={open} disabled={busy}>...</button>}
 *   </SignInAsVendorButton>
 *
 * Renders nothing unless the signed-in user is a Lusuku super user; the server
 * refuses everyone else anyway, so hiding it is presentation, not the boundary.
 * With no `children` it renders a default button styled by `className`.
 *
 * @param {string}   ownerId     the vendor's login (Users id): `owner` on every
 *                               storefront collection
 * @param {string}   [vendorId]  the storefront clicked, when the login owns more
 *                               than one
 * @param {string}   [vendorName]
 */
const isSuperUser = (user) =>
  user?.result?.userType === "LUSUKU STAFF" && user?.result?.user_role === "SUPERUSER";

const SignInAsVendorButton = ({ ownerId, vendorId, vendorName, className, style, children }) => {
  const [busy, setBusy] = useState(false);
  const user = getUserFromLocalStorage();

  if (!isSuperUser(user) || !ownerId) return null;
  // Already viewing as someone: the banner's Return is the only way out.
  if (impersonation.active()) return null;

  const open = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await apiStartVendorImpersonation(ownerId, vendorId);
      impersonation.begin(data);
      window.location.assign(resolveLandingRoute(data.result));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not sign in as this vendor.");
      setBusy(false);
    }
  };

  if (typeof children === "function") return children({ open, busy });

  return (
    <button
      type="button"
      className={className}
      style={style}
      onClick={open}
      disabled={busy}
      title={`Open ${vendorName || "this vendor"}'s own dashboard as its owner. Audited; 60 minutes; Return to admin from the banner.`}
    >
      <FaUserSecret style={{ fontSize: 10 }} /> {busy ? "Opening..." : "Sign in as vendor"}
    </button>
  );
};

export default SignInAsVendorButton;
